import type { Cell, MapConfig, PlacedTower, GridCoord } from '../interfaces/index.js'

export class Grid {
  readonly cols: number
  readonly rows: number
  private cells: Cell[][]
  private towers: Map<string, PlacedTower>

  constructor(cols: number, rows: number) {
    this.cols = cols
    this.rows = rows
    this.cells = []
    this.towers = new Map()
  }

  init(map: MapConfig): void {
    this.cells = []
    this.towers = new Map()

    for (let gy = 0; gy < this.rows; gy++) {
      const row: Cell[] = []
      for (let gx = 0; gx < this.cols; gx++) {
        row.push({ x: gx, y: gy, type: 'empty', walkable: true })
      }
      this.cells.push(row)
    }

    for (const obs of map.obstacles) {
      const cell = this.cells[obs.gy]?.[obs.gx]
      if (cell !== undefined) {
        cell.type = 'obstacle'
        cell.walkable = false
      }
    }

    const spawn = this.cells[map.spawnY]?.[map.spawnX]
    if (spawn !== undefined) {
      spawn.type = 'spawn'
      spawn.walkable = true
    }

    const base = this.cells[map.baseY]?.[map.baseX]
    if (base !== undefined) {
      base.type = 'base'
      base.walkable = true
    }
  }

  getCell(gx: number, gy: number): Cell | null {
    if (gx < 0 || gx >= this.cols || gy < 0 || gy >= this.rows) return null
    return this.cells[gy]?.[gx] ?? null
  }

  placeTower(gx: number, gy: number, tower: PlacedTower): boolean {
    const cell = this.getCell(gx, gy)
    if (cell === null) return false
    if (cell.type === 'obstacle' || cell.type === 'spawn' || cell.type === 'base') return false
    if (cell.type === 'tower') return false

    cell.type = 'tower'
    cell.towerId = tower.instanceId

    if (tower.terrain === true) {
      cell.walkable = true
    } else {
      cell.walkable = false
    }

    this.towers.set(`${gx},${gy}`, tower)
    return true
  }

  removeTower(gx: number, gy: number): boolean {
    const cell = this.getCell(gx, gy)
    if (cell === null) return false
    if (cell.type !== 'tower') return false

    cell.type = 'empty'
    cell.walkable = true
    delete cell.towerId

    this.towers.delete(`${gx},${gy}`)
    return true
  }

  hasValidPath(spawnX: number, spawnY: number, baseX: number, baseY: number): boolean {
    const visited: boolean[][] = []
    for (let gy = 0; gy < this.rows; gy++) {
      visited.push(new Array<boolean>(this.cols).fill(false))
    }

    const queue: GridCoord[] = [{ gx: spawnX, gy: spawnY }]
    const startVisited = visited[spawnY]
    if (startVisited === undefined) return false
    startVisited[spawnX] = true

    const dirs: GridCoord[] = [
      { gx: 0, gy: -1 },
      { gx: 0, gy: 1 },
      { gx: -1, gy: 0 },
      { gx: 1, gy: 0 },
    ]

    while (queue.length > 0) {
      const current = queue.shift()
      if (current === undefined) break

      if (current.gx === baseX && current.gy === baseY) return true

      for (const dir of dirs) {
        const nx = current.gx + dir.gx
        const ny = current.gy + dir.gy

        if (nx < 0 || nx >= this.cols || ny < 0 || ny >= this.rows) continue

        const visitedRow = visited[ny]
        if (visitedRow === undefined) continue
        if (visitedRow[nx] === true) continue

        const cell = this.cells[ny]?.[nx]
        if (cell === undefined || !cell.walkable) continue

        visitedRow[nx] = true
        queue.push({ gx: nx, gy: ny })
      }
    }

    return false
  }

  getWalkableCells(): Cell[][] {
    return this.cells.map(row => row.map(cell => ({ ...cell })))
  }

  /** Zero-copy read-only view of the cell grid. Use from render layer only — do not mutate. */
  getCells(): ReadonlyArray<ReadonlyArray<Readonly<Cell>>> {
    return this.cells
  }

  /** Returns all currently-placed towers as an array. O(n) in number of towers. */
  getTowers(): PlacedTower[] {
    return [...this.towers.values()]
  }

  getTowerAt(gx: number, gy: number): PlacedTower | undefined {
    return this.towers.get(`${gx},${gy}`)
  }

  serialize(): Cell[][] {
    return this.cells.map(row => row.map(cell => ({ ...cell })))
  }

  static fromSerialized(data: Cell[][]): Grid {
    const rows = data.length
    const cols = rows > 0 ? (data[0]?.length ?? 0) : 0
    const grid = new Grid(cols, rows)
    grid.cells = data.map(row => row.map(cell => ({ ...cell })))
    return grid
  }
}

export function cartToIso(
  gx: number,
  gy: number,
  cellW: number,
  cellH: number
): { x: number; y: number } {
  return {
    x: (gx - gy) * (cellW / 2),
    y: (gx + gy) * (cellH / 2),
  }
}

export function isoToCart(
  px: number,
  py: number,
  cellW: number,
  cellH: number,
  cols?: number,
  rows?: number
): { gx: number; gy: number } {
  // From cartToIso:
  //   px = (gx - gy) * (cellW / 2)  =>  px / (cellW/2) = gx - gy
  //   py = (gx + gy) * (cellH / 2)  =>  py / (cellH/2) = gx + gy
  // Solving:
  //   gx = (px/(cellW/2) + py/(cellH/2)) / 2 = px/cellW + py/cellH
  //   gy = (py/(cellH/2) - px/(cellW/2)) / 2 = py/cellH - px/cellW
  //
  // Math.round (not floor) is required: cartToIso places the coordinate at the
  // top vertex of each diamond, so a tap anywhere inside the diamond must snap
  // to the nearest cell center. Math.floor shifts the hit region by half a cell
  // towards the origin, causing wrong cell detection at grid edges and borders.
  let gx = Math.round(px / cellW + py / cellH) || 0
  let gy = Math.round(py / cellH - px / cellW) || 0

  // Clamp to grid bounds when dimensions are provided to prevent out-of-bounds
  // coordinates at grid edges (known bug: isometric mapping is imprecise at corners).
  if (cols !== undefined && rows !== undefined) {
    gx = Math.max(0, Math.min(cols - 1, gx))
    gy = Math.max(0, Math.min(rows - 1, gy))
  }

  return { gx, gy }
}
